/**
 * redux-persist migrations for the root config.
 *
 * Lives in its own module rather than inside store.ts so it can be tested:
 * importing the store pulls in languageSlice -> i18n -> expo-localization,
 * which Jest cannot transform.
 */

/** Strip the dead `translatedLanguages` stamp from every recipe entry. */
const dropTranslationStamps = (state: any) => {
  const metadata = state?.recipeMetadata?.metadata;
  if (!metadata) return state;
  return {
    ...state,
    recipeMetadata: {
      ...state.recipeMetadata,
      metadata: Object.fromEntries(
        Object.entries(metadata).map(([recipeId, entry]) => {
          const { translatedLanguages, ...rest } = (entry ?? {}) as Record<string, unknown>;
          return [recipeId, rest];
        }),
      ),
    },
  };
};

/**
 * v1 — clear every `translatedLanguages` stamp once.
 *
 * A recipe translation that silently no-opped still recorded itself as done,
 * and `recipeMetadata` is persisted, so affected recipes short-circuited the
 * translation effect on every subsequent open and stayed in the wrong language
 * permanently. Fixing the underlying bug does not release recipes already
 * stamped; only dropping the stamps does.
 *
 * Cost is bounded: the stamp is a cache hint, not user data. It is re-derived
 * from the Firestore/AsyncStorage translation caches, so the worst case is one
 * re-translation per recipe.
 *
 * Deliberately narrow — the root config covers 14 slices, so this rebuilds only
 * `recipeMetadata.metadata` and passes everything else through by reference.
 */
export const persistMigrations = {
  1: dropTranslationStamps,
  /**
   * v2 — drop the dead `pantrySync` prompt keys.
   *
   * `shopDay` could only ever be set by `PantrySyncDeclarationCard`, which
   * #423 rendered and the #468 weekly redesign removed. `pantrySync` stayed in
   * the persist whitelist, so a day picked in that three-week window survived
   * in storage forever with no UI left to change it, and kept
   * `PantrySyncPromptSheet` firing from that weekday through Sunday on every
   * page load (#641).
   *
   * The prompt is gone, so these keys drive nothing today. They are stripped
   * anyway: redux-persist merges the persisted slice over `initialState` at
   * level 1, so leaving them means every affected user carries a `shopDay`
   * into any future pantry work — where the obvious mistake is to read it and
   * resurrect the bug. `lastUpdatedAt` is real and stays.
   */
  2: (state: any) => {
    const pantrySync = state?.pantrySync;
    if (!pantrySync) return state;
    return {
      ...state,
      pantrySync: { lastUpdatedAt: pantrySync.lastUpdatedAt ?? null },
    };
  },
  /**
   * v3 — clear the `translatedLanguages` stamps again, and for the last time.
   *
   * v1 dropped the stamps but left the mechanism standing, so the screen went
   * on stamping every recipe it translated and went on skipping the
   * translation effect whenever it found a stamp. That skip is the bug: the
   * translation lives in the translation caches, never on the recipe, and the
   * screen reloads the recipe in its ORIGINAL language every time — so
   * "already translated" meant "render English inside a French app, forever".
   *
   * The stamp is gone from the slice now, and nothing reads it. This clears
   * what v1-era and later storage still carries so the field cannot be
   * rediscovered and re-wired.
   */
  3: dropTranslationStamps,
  /**
   * v4 — the Du Chef onboarding replaced the welcome / diet / fridge flow.
   *
   * The old `onboarding` slice carried `familySizeSelected`, `generatedMeals`,
   * fridge-snap flags and a six-value phase union. None of it maps onto the
   * new state (three duels → allergies → a direction), so the slice is rebuilt
   * from its initial shape and only the one fact that matters survives:
   * `completed`. A cook who finished the old flow never sees the new one; a
   * cook caught mid-flow starts the new quiz.
   */
  4: (state: any) => {
    const old = state?.onboarding;
    if (!old) return state;
    const completed = !!old.completed;
    return {
      ...state,
      onboarding: {
        completed,
        currentPhase: completed ? 'done' : 'quiz',
        startedAt: null,
        seed: null,
        variant: 0,
        answers: [],
        picks: [],
        safety: null,
        generatedWeek: [],
        onboardingWeekStart: null,
        direction: null,
        readingCardDismissed: false,
        nudges: {
          signupShown: !!old.nudges?.signupShown,
          signupDismissed: !!old.nudges?.signupDismissed,
          pantryReminderShown: !!old.nudges?.pantryReminderShown,
        },
      },
    };
  },
};

/** Bump alongside a new numbered migration above. */
export const PERSIST_VERSION = 4;
